import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';
import { useUpdateTotalNotification } from '../../utils/useUpdateTotalNotification';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link, useHistory, useParams } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import GlobalModal from '../../components/GlobalModal';
import CommentModal from './CommentModal';
import AttachmentModal from './AttachmentModal';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as CommentIcon } from '../../assets/svg/comments.svg';
import { ReactComponent as AttachmentIcon } from '../../assets/svg/attachment.svg';
import { ReactComponent as TickIcon } from '../../assets/svg/tick-icon.svg';
import { ReactComponent as PlusIcon } from '../../assets/svg/plus-icon.svg';
import { ReactComponent as QuestionMarkIcon } from '../../assets/svg/questionmark.svg';
import { ReactComponent as DownloadIcon } from '../../assets/svg/download.svg';

// TYPES
import { RouteParams } from '../../Interfaces';
import {
	DomainChecklistActionStatus,
	SafetyChecklistChecklistSafetyChecklist,
	DomainChecklistStatusType
} from '../../api/models';

const SafetyChecklistsDetail: React.FC = () => {
	const history = useHistory();

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Get Id from params
	const params = useParams<RouteParams>();
	const checklistId = params.id;

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Set up Data state and Initial Data
	const initialData = {
		id: undefined,
		identifier: undefined,
		purpose: undefined,
		template: undefined,
		status: undefined,
		creator: undefined,
		statusDescription: undefined,
		additionalComments: undefined,
		checklistItems: []
	};

	const [safetyChecklistsDetailData, setSafetyChecklistsDetailData] =
		React.useState<SafetyChecklistChecklistSafetyChecklist>(initialData);

	const getSafetyChecklistsDetailData = async (useLoading: boolean = true) => {
		if (useLoading) {
			setIsLoading(true);
		}

		const response = await apibridge.getSafetyChecklistChecklist(checklistId);

		response.result = response.result || initialData;

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setSafetyChecklistsDetailData(response.result);
		}
	};

	React.useEffect(() => {
		getSafetyChecklistsDetailData();
	}, []);

	const {
		id,
		identifier,
		creator,
		status,
		statusDescription,
		template,
		canApprove,
		canEdit,
		checklistItems,
		purpose,
		additionalComments
	} = safetyChecklistsDetailData!;

	// Submit form data
	const onFormSubmit = async (values: any) => {
		setIsLoading(true);

		const submitData = {
			id: params.id,
			additionalComments: values.additionalCommentsInput
		};

		const response = !canApprove
			? await apibridge.postSafetyChecklistSubmit(submitData)
			: await apibridge.postSafetyChecklistApprove(submitData);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			history.push('/safety-checklists');

			useUpdateTotalNotification();
		}
	};

	// Validation
	const validationSchema = Yup.object({
		additionalCommentsInput: Yup.string().required('Required')
	});

	// Initial values
	const initialDetailFormValues = {
		additionalCommentsInput: additionalComments
	};

	// Update Checklist status
	const updateStatus = async (id: string, status: DomainChecklistActionStatus) => {
		const checklistStatusData = {
			id: id,
			status: status
		};

		const response = await apibridge.postSafetyChecklistAction(checklistStatusData);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);
		}
	};

	// OnChange Handler logic for checklist Items
	const onChangeYesHandler = (id: string) => {
		updateStatus(id, 'Yes');

		const newData = { ...safetyChecklistsDetailData };

		const { checklistItems } = newData;

		const newCheckListItems = checklistItems?.map((item) => {
			if (item.id === id) {
				return {
					...item,
					status: DomainChecklistActionStatus.Yes
				};
			} else {
				return {
					...item
				};
			}
		});

		const finalData = { ...newData, checklistItems: newCheckListItems };

		setSafetyChecklistsDetailData(finalData);
	};

	const onChangeNoHandler = (id: string) => {
		updateStatus(id, 'No');

		const newData = { ...safetyChecklistsDetailData };

		const { checklistItems } = newData;

		const newCheckListItems = checklistItems?.map((item) => {
			if (item.id === id) {
				return {
					...item,
					status: DomainChecklistActionStatus.No
				};
			} else {
				return {
					...item
				};
			}
		});

		const finalData = { ...newData, checklistItems: newCheckListItems };

		setSafetyChecklistsDetailData(finalData);
	};

	const onChangeNAHandler = (id: string) => {
		updateStatus(id, 'NotApplicable');

		const newData = { ...safetyChecklistsDetailData };

		const { checklistItems } = newData;

		const newCheckListItems = checklistItems?.map((item) => {
			if (item.id === id) {
				return {
					...item,
					status: DomainChecklistActionStatus.NotApplicable
				};
			} else {
				return {
					...item
				};
			}
		});

		const finalData = { ...newData, checklistItems: newCheckListItems };

		setSafetyChecklistsDetailData(finalData);
	};

	// State to manage Modal and pass in id
	const [isMounted, setIsMounted] = React.useState<boolean>(false);

	const [showCommentModal, setShowCommentModal] = React.useState<boolean>(false);

	const [commentId, setCommentId] = React.useState<string>('');

	const commentModalControl = (itemId: string | undefined) => {
		if (itemId !== undefined) {
			setCommentId(itemId);
			setShowCommentModal(true);
		}
	};

	React.useEffect(() => {
		if (showCommentModal === false && isMounted === true) {
			getSafetyChecklistsDetailData(false);
		}

		setIsMounted(true);
	}, [showCommentModal]);

	const [showAttachmentModal, setShowAttachmentModal] = React.useState<boolean>(false);

	const [attachmentId, setAttachmentId] = React.useState<string>('');

	const attachmentModalControl = (itemId: string | undefined) => {
		if (itemId !== undefined) {
			setAttachmentId(itemId);
			setShowAttachmentModal(true);
		}
	};

	React.useEffect(() => {
		if (showAttachmentModal === false && isMounted === true) {
			getSafetyChecklistsDetailData(false);
		}

		setIsMounted(true);
	}, [showAttachmentModal]);

	return (
		<div className="page-general-layout">
			<Formik
				initialValues={initialDetailFormValues}
				validationSchema={validationSchema}
				onSubmit={onFormSubmit}
				enableReinitialize
			>
				{(formik) => {
					return (
						<Form translate="yes" className="general-form">
							{!!formErrorMessage ? (
								<div className="form-validation-error">{formErrorMessage}</div>
							) : (
								<SwitchTransition mode="out-in">
									<CSSTransition
										key={`key_${isLoading}`}
										timeout={600}
										classNames="standalone-fade"
										mountOnEnter
										unmountOnExit
									>
										{isLoading ? (
											<LoadingSpinner show={isLoading} />
										) : (
											<>
												<div className="general-form-layout-wrapper">
													<div className="form-main w-100">
														<p className="sub-grey-text offset-top-70">Checklist purpose</p>

														<h1 className="text-capitalized mbtom-20">{purpose}</h1>

														<p className="sub-grey-text mbtom-0">
															Mark items off your checklist and once completed submit your checklist for approval.
														</p>

														<p className="sub-grey-text mbtom-30">
															Once submitted your checklist will be sent to your nominated staff member for approval.
														</p>

														<div className="complaints-detail-general-info">
															<div className="general-info-left">
																<div className="info-col">
																	<div className="info-title">Created by</div>

																	<div className="info-detail">{creator}</div>
																</div>

																<div className="info-col">
																	<div className="info-title">ID</div>

																	<div className="info-detail">{identifier}</div>
																</div>

																<div className="info-col">
																	<div className="info-title">Template</div>

																	<div className="info-detail">{template ? template : 'None'}</div>
																</div>

																<div className="info-col">
																	<div className="info-title">Status</div>

																	<div className="info-detail">{statusDescription}</div>
																</div>
															</div>

															<div className="general-info-right">
																{isOrganisationActive && (
																	<Link
																		className={canEdit ? 'btn-primary resolve-btn' : 'btn-primary resolve-btn disabled'}
																		to={`/create-checklist/${id}`}
																	>
																		Edit checklist
																	</Link>
																)}
															</div>
														</div>

														<h3 className="mtop-80 mbtom-16">Checklist items</h3>

														<ul>
															{checklistItems &&
																checklistItems.length > 0 &&
																checklistItems.map((item, index) => {
																	return (
																		<li className="list-item-with-details" key={index}>
																			<div className="detail-left">{item.text}</div>
																			<div className="detail-right">
																				<div className="detail-group">
																					<input
																						disabled={!canEdit || !isOrganisationActive}
																						type="radio"
																						name={item.id}
																						value="Yes"
																						checked={item.status === 'Yes'}
																						onChange={() => onChangeYesHandler(item.id!)}
																					/>
																					<label htmlFor={item.id}>Yes</label>
																				</div>

																				<div className="detail-group">
																					<input
																						disabled={!canEdit || !isOrganisationActive}
																						type="radio"
																						name={item.id}
																						value="No"
																						checked={item.status === 'No'}
																						onChange={() => onChangeNoHandler(item.id!)}
																					/>
																					<label htmlFor={item.id}>No</label>
																				</div>

																				<div className="detail-group">
																					<input
																						disabled={!canEdit || !isOrganisationActive}
																						type="radio"
																						name={item.id}
																						value="NotApplicable"
																						checked={item.status === 'NotApplicable'}
																						onChange={() => onChangeNAHandler(item.id!)}
																					/>
																					<label htmlFor={item.id}>N/A</label>
																				</div>

																				<Button
																					className={`icon-group ${item.hasComment ? 'active' : ''}`}
																					onClick={() => commentModalControl(item.id)}
																					disabled={!isOrganisationActive}
																				>
																					<CommentIcon className="content-icon" />

																					{item.hasComment ? (
																						<div className="icon-wrapper show">
																							<TickIcon className="info-icon-small " />
																						</div>
																					) : (
																						<div className="icon-wrapper">
																							<PlusIcon className="info-icon" />
																						</div>
																					)}
																				</Button>

																				<Button
																					className={`icon-group ${item.attachments !== 0 ? 'active' : ''}`}
																					onClick={() => attachmentModalControl(item.id)}
																					disabled={!isOrganisationActive}
																				>
																					<AttachmentIcon className="content-icon" />

																					{item.attachments === 0 ? (
																						<div className="icon-wrapper">
																							<PlusIcon className="info-icon" />
																						</div>
																					) : (
																						<div className="icon-wrapper has-number">
																							<div className="info-icon">{item.attachments}</div>
																						</div>
																					)}
																				</Button>
																			</div>
																		</li>
																	);
																})}
														</ul>

														<div className="form-group mtop-40">
															<label htmlFor="additionalCommentsInput" className="label">
																Additional Comments
															</label>

															<div className="form-field">
																<Field
																	disabled={
																		safetyChecklistsDetailData.status === DomainChecklistStatusType.AwaitingApproval ||
																		safetyChecklistsDetailData.status === DomainChecklistStatusType.Approved ||
																		!safetyChecklistsDetailData.canEdit ||
																		!isOrganisationActive
																	}
																	as="textarea"
																	name="additionalCommentsInput"
																	id="additionalCommentsInput"
																	type="text"
																	className="form-input"
																/>

																<ErrorMessage
																	name="additionalCommentsInput"
																	component="div"
																	className="error-message"
																/>
															</div>
														</div>
													</div>
												</div>

												{safetyChecklistsDetailData.status === DomainChecklistStatusType.Approved ? (
													<a href={safetyChecklistsDetailData.summaryPdfUrl} download className="btn-secondary">
														<DownloadIcon /> Download Checklist Summary
													</a>
												) : canApprove ? (
													<>
														<div className="general-divider"></div>

														{isOrganisationActive && (
															<div className="form-button-group">
																<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
																	Approve
																</Button>
															</div>
														)}
													</>
												) : null}

												{canEdit ? (
													<>
														<div className="general-divider"></div>

														<div className="form-button-group">
															<Button
																type="submit"
																disabled={
																	safetyChecklistsDetailData.status === DomainChecklistStatusType.AwaitingApproval ||
																	!isOrganisationActive
																}
																className="btn-primary submit-btn complaints-submit-page"
															>
																Submit for approval
															</Button>
														</div>
													</>
												) : null}

												{canApprove ? (
													<div className="general-form-guide wide">
														<div className="guide-left">
															<QuestionMarkIcon />
														</div>

														<div className="guide-right">
															<h2 className="guide-heading">Not ready to approve this checklist?</h2>

															<p className="guide-content">
																Please discuss any required changes to this checklist with the staff member who created
																it.
															</p>
														</div>
													</div>
												) : null}
											</>
										)}
									</CSSTransition>
								</SwitchTransition>
							)}
						</Form>
					);
				}}
			</Formik>

			<GlobalModal isOpen={showCommentModal} setIsOpen={setShowCommentModal} titleText="Comment">
				<CommentModal id={commentId} canEdit={canEdit} />
			</GlobalModal>

			<GlobalModal isOpen={showAttachmentModal} setIsOpen={setShowAttachmentModal} titleText="Attachments">
				<AttachmentModal attachmentId={attachmentId} canEdit={canEdit} />
			</GlobalModal>
		</div>
	);
};

export default SafetyChecklistsDetail;
